import classNames from "classnames";
import { BankSelector } from "components/BankSelector/BankSelector";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import Table from "components/DesignSystem/Table/V2/Table";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { DeleteIcon } from "components/icons/delete";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { DuplicateIcon } from "components/icons/DuplicateIcon";
import { MagnifyingGlass } from "components/icons/MagnifyingGlass";
import { MediumInfo } from "components/icons/MediumInfo";
import { PriceInput } from "components/PriceInput/PriceInput";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";
import {
  MerchantComponent,
  MerchantSelector,
} from "components/Transaction/MerchantSelector";
import { FormBasedClassificationCell } from "../FormClassification/FormClassifications";
import { ACCOUNTING_METHODS } from "constants/accountMethod";
import { useFormikContext } from "formik";
import { AnimatePresence, motion } from "framer-motion";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";

import { EmptyScreen } from "pages/Books/EmptyScreen";
import randomBytes from "randombytes";
import { useMemo, useState } from "react";
import { SingleValue } from "react-select";
import {
  CellContext,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "react-table-8.10.7";
import {
  useCreateMerchantMutation,
  useGetMerchantByIdQuery,
} from "store/apis/merchant";
import { useUploadManualTransactionInvoiceMutation } from "store/apis/transactions";
import { BackendError } from "types/utils/error";
import {
  getCategoryFieldName,
  getCategoryId,
  getNewJournalEntryId,
  isCashOnlyTransaction,
  recalculateSerialNumbers,
} from "utils/journalEntryUtils";
import { Preview } from "components/PreviewModal";
import { roundToFixedDecimal } from "utils/roundtoFixedDecimal";
import { validate } from "uuid";
import {
  initialTransaction,
  JournalEntryTransaction,
} from "./AddJournalEntryModal";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import { ModalDescriptionWrapper } from "pages/Books/RevenueMetrics/RevenueMetricsLocked";

export const Vendor = ({
  id,
  merchant,
  creatable = false,
}: {
  id: string;
  merchant?: string | null;
  creatable?: boolean;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { setFieldValue, values: formValues } = useFormikContext<{
    transactions: JournalEntryTransaction[];
  }>();

  const { data, isLoading } = useGetMerchantByIdQuery(
    { groupId, merchantId: merchant || "" },
    { skip: !groupId || !merchant }
  );
  const { logo, name = "" } = data || {};

  const selected = merchant
    ? {
        value: name,
        label: <MerchantComponent logo={logo} name={name} />,
        merchantId: merchant,
        data: name,
      }
    : null;

  const merchantChange = async (values: SingleValue<any>) => {
    setFieldValue(`transactions[${id}].merchant`, values?.merchantId || null);
  };

  const { alertToast } = useToast();
  const [createMerchant] = useCreateMerchantMutation();

  const onCreateMerchant = async (name: string) => {
    try {
      const index = Number(id);
      const { transactions } = formValues;
      const txn_uuid = transactions[index].uuid;
      const payload = new FormData();

      payload.append("name", name);

      if (txn_uuid && validate(txn_uuid)) {
        payload.append("txn_uuid", txn_uuid);
      }

      const data = await createMerchant({
        groupId,
        entityId,
        payload,
      }).unwrap();

      setFieldValue(`transactions[${id}].merchant`, data.merchant_id);
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <MerchantSelector
      isLoading={isLoading}
      isDisabled={isLoading}
      merchantChange={merchantChange}
      onCreateMerchant={onCreateMerchant}
      withForm
      size="small"
      name={`transactions[${id}].merchant`}
      value={selected}
      creatable={creatable}
    />
  );
};

export const BankAccount = ({
  id,
  account_id,
}: {
  id: string;
  account_id: string;
}) => {
  return (
    <BankSelector
      name={`transactions[${id}].account_id`}
      withForm
      placeholder="Select"
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({
          ...base,
          width: 450,
        }),
      }}
    />
  );
};

export const Description = (
  info: CellContext<JournalEntryTransaction, string>
) => {
  const id = info.row.id;

  return (
    <TextInput
      name={`transactions[${id}].description`}
      placeholder="Enter description"
      hideError
      showErrorOnceTouched
      customSize="small"
    />
  );
};

export const Invoice = ({
  id,
  invoice,
}: {
  id: string;
  invoice: JournalEntryTransaction["invoice"];
}) => {
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const [uploadManualTransactionInvoice, { isLoading: isUploading }] =
    useUploadManualTransactionInvoiceMutation();

  const { setFieldValue } = useFormikContext<{
    transactions: JournalEntryTransaction[];
  }>();

  const onDrop = async (files: FileType[]) => {
    try {
      const file = await uploadManualTransactionInvoice({
        invoice: files[0] as File,
        entityId,
        groupId,
      }).unwrap();
      setFieldValue(`transactions[${id}].invoice`, file);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const onInvoiceDelete = () => {
    setFieldValue(`transactions[${id}].invoice`, null);
  };

  return (
    <>
      <FileInput
        file={
          invoice
            ? {
                uuid: invoice.uuid,
                name: invoice?.name,
                file_type: invoice?.file_type,
                is_previewable: invoice?.is_previewable,
              }
            : null
        }
        onDrop={onDrop}
        isUploading={isUploading}
        variant="icon"
        onDelete={onInvoiceDelete}
        onFileClick={({ uuid: fileId }) => setPreviewFileId(fileId)}
      />
      <Preview
        showModal={Boolean(previewFileId)}
        closeModal={() => setPreviewFileId(null)}
        groupId={groupId}
        previewId={previewFileId}
      />
    </>
  );
};

export const Delete = ({
  id,
  disabled,
  maxSerialNumber,
}: {
  id: string;
  disabled?: boolean;
  maxSerialNumber?: string;
}) => {
  const { values, setFieldValue } = useFormikContext<{
    transactions: JournalEntryTransaction[];
  }>();

  const onDelete = () => {
    const newValues = values.transactions.filter((transaction) => {
      return transaction.id !== id;
    });
    const updatedValues = maxSerialNumber
      ? recalculateSerialNumbers(newValues, maxSerialNumber)
      : newValues;
    setFieldValue("transactions", updatedValues);
  };

  return (
    <Button
      type="button"
      customType="ghost_icon"
      onClick={onDelete}
      disabled={disabled}
    >
      <DeleteIcon />
    </Button>
  );
};

export const Duplicate = ({
  id,
  maxSerialNumber,
}: {
  id: string;
  maxSerialNumber?: string;
}) => {
  const { values, setFieldValue } = useFormikContext<{
    transactions: JournalEntryTransaction[];
  }>();

  const onDuplicate = () => {
    const fromIndex = values.transactions.findIndex(
      (transaction) => transaction.id === id
    );

    if (fromIndex === -1) {
      return;
    }

    const duplicatedEntry = {
      ...values.transactions[fromIndex],
      id: getNewJournalEntryId(),
      uuid: getNewJournalEntryId(),
      ledger_entry_serial_number: "",
      invoice: null,
    };
    const newValues = values.transactions.toSpliced(
      fromIndex + 1,
      0,
      duplicatedEntry
    );
    const updatedValues = maxSerialNumber
      ? recalculateSerialNumbers(newValues, maxSerialNumber)
      : newValues;
    setFieldValue("transactions", updatedValues);
  };

  return (
    <Button
      type="button"
      customType="ghost_icon"
      onClick={onDuplicate}
      aria-label="Duplicate line item"
    >
      <ToolTip disableHoverableContent text="Duplicate line item">
        <span className="t-text-text-30">
          <DuplicateIcon />
        </span>
      </ToolTip>
    </Button>
  );
};

export const Amount = ({
  id,
  type,
}: {
  id: string;
  type?: "debit" | "credit";
}) => {
  const { setFieldValue } = useFormikContext<{
    transactions: JournalEntryTransaction[];
  }>();

  const onValueChange = () => {
    const inversType = type === "debit" ? "credit" : "debit";
    setFieldValue(`transactions[${id}].${inversType}`, 0);
  };

  return (
    <PriceInput
      aria-label={`${type} input`}
      name={`transactions[${id}].${type}`}
      hideError
      rightAlign
      allowNegative={false}
      onValueChange={onValueChange}
      placeholder={`transactions[${id}].${type}`}
      customSize="small"
    />
  );
};

const createColumn = createColumnHelper<JournalEntryTransaction>();

const columns = [
  createColumn.accessor("ledger_entry_serial_number", {
    size: 5,
    header: () => (
      <div className="t-flex t-items-center t-gap-1.5">
        <Label className="!t-pb-0">ID</Label>
        <ToolTip
          text={
            <div className="t-text-left t-text-body-sm t-w-60">
              Unique ID allotted to each journal entry’s transaction, retained
              even after deletion for tracking purposes
            </div>
          }
        >
          <Label className="!t-pb-0 t-flex t-items-center">
            <MediumInfo size={14} />
          </Label>
        </ToolTip>
      </div>
    ),
    cell: (info) => {
      const { ledger_entry_serial_number } = info.row.original || {};
      return (
        <span className="t-text-text-30">
          {ledger_entry_serial_number || "-"}
        </span>
      );
    },
  }),
  createColumn.accessor("description", {
    size: 15,
    header: (header) => (
      <HeadCellContent
        title="Description"
        required
        sort={{
          nextSortOrder: header.column.getNextSortingOrder(),
          isSorted: header.column.getIsSorted(),
          sortingHandler: header.column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: Description,
  }),

  createColumn.accessor("merchant", {
    size: 15,
    header: () => <Label>VENDOR</Label>,
    cell: (info) => {
      const id = info.row.id;
      const { merchant = "" } = info.row.original || {};

      return <Vendor id={id} merchant={merchant} creatable />;
    },
  }),

  createColumn.accessor(
    (row) => {
      const isCashEntry = isCashOnlyTransaction(row);
      return getCategoryId(row, isCashEntry) ?? "";
    },
    {
      id: "category",
      size: 20,
      header: (header) => (
        <HeadCellContent
          title="Category"
          required
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => {
        const id = info.row.id;
        const transaction = info.row.original;

        // Determine if this is a cash or accrual entry based on basisEntries
        const isCashEntry = isCashOnlyTransaction(transaction);
        const category = getCategoryId(transaction, isCashEntry);
        const fieldName = getCategoryFieldName(id, isCashEntry);

        return (
          <CategorySelector
            name={fieldName}
            category={category || ""}
            hiddenCategoryTypes={[]}
            size="small"
          />
        );
      },
    }
  ),

  createColumn.accessor("classification_ids", {
    id: "classification_ids",
    size: 5,
    header: (info) => {
      const { subscriptionModal } = info.table.options.meta as {
        subscriptionModal: ReturnType<typeof useModal>;
      };

      return (
        <div className="t-flex t-justify-end">
          <Label className="!t-pb-0">
            <div className="t-flex t-items-center t-gap-1">
              <span>Classes</span>
              <PermissionBasedUI
                feature={CLASSIFICATION_FEATURE_ACCESS}
                onUpgrade={() => subscriptionModal?.open()}
                blockedUI={<DiamondPurple height={13} width={14} />}
              >
                <></>
              </PermissionBasedUI>
            </div>
          </Label>
        </div>
      );
    },
    cell: (info) => {
      const transactionIndex = info.row.id;
      const { entityId, subscriptionModal } = info.table.options.meta as {
        entityId: string;
        subscriptionModal: ReturnType<typeof useModal>;
      };

      return (
        <PermissionBasedUI
          feature={CLASSIFICATION_FEATURE_ACCESS}
          onUpgrade={() => subscriptionModal?.open()}
          blockedUI={
            <FormBasedClassificationCell
              transactionIndex={transactionIndex}
              entityId={entityId}
              isDisabled
            />
          }
        >
          <FormBasedClassificationCell
            transactionIndex={transactionIndex}
            entityId={entityId}
          />
        </PermissionBasedUI>
      );
    },
  }),
  createColumn.accessor("debit", {
    size: 10,
    header: (header) => (
      <HeadCellContent
        title="Debit"
        required
        align="left"
        sort={{
          nextSortOrder: header.column.getNextSortingOrder(),
          isSorted: header.column.getIsSorted(),
          sortingHandler: header.column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: (info) => {
      const id = info.row.id;

      return <Amount id={id} type="debit" />;
    },
  }),

  createColumn.accessor("credit", {
    size: 10,
    header: (header) => (
      <HeadCellContent
        title="Credit"
        required
        align="left"
        sort={{
          nextSortOrder: header.column.getNextSortingOrder(),
          isSorted: header.column.getIsSorted(),
          sortingHandler: header.column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: (info) => {
      const id = info.row.id;

      return <Amount id={id} type="credit" />;
    },
  }),

  createColumn.accessor("invoice", {
    size: 5,
    header: "",
    cell: (info) => {
      const id = info.row.id;
      const invoice = info.getValue();

      return (
        <Invoice
          id={id}
          invoice={
            invoice
              ? {
                  file_type: invoice?.file_type || "IMAGE",
                  is_previewable: invoice?.is_previewable || false,
                  name: invoice?.name || "",
                  uuid: invoice?.uuid || "",
                }
              : null
          }
        />
      );
    },
  }),

  createColumn.accessor("id", {
    size: 10,
    header: "",
    cell: (info) => {
      const { id } = info.row.original;
      const { maxSerialNumber } = info.table.options.meta as {
        maxSerialNumber: string;
      };
      return (
        <div className="t-flex t-gap-1">
          <Duplicate id={id} maxSerialNumber={maxSerialNumber} />
          <Delete id={id} maxSerialNumber={maxSerialNumber} />
        </div>
      );
    },
  }),
];

export const AddJournalEntryTable = ({
  allEntries,
  basisEntries = allEntries,
  entryType,
  maxSerialNumber,
}: {
  allEntries: JournalEntryTransaction[];
  basisEntries?: JournalEntryTransaction[];
  entryType?: "CASH" | "ACCRUAL";
  maxSerialNumber: string;
}) => {
  const entityId = useCurrentEntityId();
  const subscriptionModal = useModal();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { setFieldValue, values } = useFormikContext<{
    transactions: JournalEntryTransaction[];
    basis?: string;
  }>();

  const transactions = basisEntries;

  const addAnotherTransaction = () => {
    let shouldBeCashEntry = false;

    if (entryType) {
      shouldBeCashEntry = entryType === ACCOUNTING_METHODS.CASH;
    } else if (basisEntries.length > 0) {
      shouldBeCashEntry = basisEntries.some(isCashOnlyTransaction);
    } else if (values.basis) {
      // Only check basis if it exists (not all usages have basis field, e.g., PayrollJE)
      shouldBeCashEntry = values.basis === ACCOUNTING_METHODS.CASH;
    }
    // If all checks fail, defaults to accrual entry (shouldBeCashEntry = false)

    const newValues = [
      ...allEntries,
      {
        ...initialTransaction,
        id: `${randomBytes(10).toString("hex")}NEW_ENTRY`,
        uuid: `${randomBytes(10).toString("hex")}NEW_ENTRY`,
        ledger_entry_serial_number: "",
        // If this is for cash entries, set empty cash_category_id, otherwise set empty accrual_category_id
        ...(shouldBeCashEntry
          ? { cash_category_id: "", entry_type: ACCOUNTING_METHODS.CASH }
          : {
              accrual_category_id: "",
              entry_type: ACCOUNTING_METHODS.ACCRUAL,
            }),
      },
    ];
    const updatedValues = recalculateSerialNumbers(newValues, maxSerialNumber);
    setFieldValue("transactions", updatedValues);
  };

  const data = useMemo(() => transactions || [], [transactions]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
    defaultColumn: {
      minSize: 1,
      maxSize: 100,
    },
    getRowId: (row, index) =>
      allEntries.findIndex((t) => t.id === row.id).toString(),
    meta: { maxSerialNumber, entityId, subscriptionModal },
  });

  const totalDebit = roundToFixedDecimal({
    numberToRound: transactions.reduce(
      (acc, { debit }) => acc + Number(debit),
      0
    ),
  });

  const totalCredit = roundToFixedDecimal({
    numberToRound: transactions.reduce(
      (acc, { credit }) => acc + Number(credit),
      0
    ),
  });

  const noTransaction = values?.transactions?.length === 0;
  const amountDiff = Math.abs(totalDebit - totalCredit);

  return (
    <>
      {noTransaction ? (
        <EmptyScreen text="No journal entry found" className="!t-h-56">
          <span className="t-text-i-neutral-10">
            <MagnifyingGlass size="149" />
          </span>
        </EmptyScreen>
      ) : (
        <Table.Container layout="fixed" className="sm:t-overflow-x-auto">
          <Table.Content className="sm:t-min-w-[1000px]">
            <Table.Head>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.HeadCell
                      key={header.id}
                      className="t-group"
                      style={{ width: `${header.getSize()}%` }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Table.HeadCell>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
            <Table.Body>
              <AnimatePresence initial={false}>
                {table.getRowModel().rows.map((row) => (
                  <motion.tr
                    layout
                    key={row.original.id}
                    initial={{ y: -100 }}
                    animate={{ x: 0, y: 0 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ ease: "easeOut", duration: 0.1 }}
                    className="t-px-3 t-border-solid t-border-neutral-0 t-border-b t-border-0 t-text-body-sm"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}%` }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </motion.tr>
                ))}
              </AnimatePresence>
              <Table.Row
                className={classNames("!t-border-none t-sticky t-bg-white", {
                  "t-bottom-[37px]": Boolean(amountDiff),
                  "t-bottom-0": !Boolean(amountDiff),
                })}
              >
                <Table.Cell></Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell className="t-text-end t-p-2 t-text-body-sm t-font-medium t-text-text-30">
                  Total
                </Table.Cell>
                <Table.Cell className="t-p-2 t-text-body-sm t-font-medium t-text-right t-px-5">
                  <AmountSuperScript amount={totalDebit} />
                </Table.Cell>
                <Table.Cell className="t-p-2 t-text-body-sm t-font-medium t-text-right t-px-5">
                  <AmountSuperScript amount={totalCredit} />
                </Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell></Table.Cell>
              </Table.Row>
              {Boolean(amountDiff) && (
                <Table.Row className="!t-border-none t-sticky t-bottom-0 t-bg-white">
                  <Table.Cell></Table.Cell>
                  <Table.Cell></Table.Cell>
                  <Table.Cell></Table.Cell>
                  <Table.Cell></Table.Cell>

                  <Table.Cell className=" t-text-end t-text-body-sm t-font-medium t-text-red">
                    Difference
                  </Table.Cell>
                  <Table.Cell className="t-p-2 t-text-body-sm t-font-medium t-px-5 t-text-right t-text-red">
                    <AmountSuperScript amount={amountDiff} />
                  </Table.Cell>
                  <Table.Cell></Table.Cell>

                  <Table.Cell></Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      )}
      <div className="t-bg-white t-bottom-0 t-flex t-items-center t-gap-2">
        <Button type="button" size="small" onClick={addAnotherTransaction}>
          Add
          {noTransaction ? " item" : " more"}
        </Button>
      </div>
      <BooksSoftwareSubscriptionsModal
        open={subscriptionModal.isOpen}
        onClose={() => subscriptionModal.close()}
        entityId={entityId}
        onProductSubscribe={() => subscriptionModal.close()}
        modalDescription={
          <ModalDescriptionWrapper value="Unlock Classifications and more with Books Standard Plan" />
        }
      />
    </>
  );
};
